import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

interface AdminNavItem {
  name: string;
  path: string;
  icon: string;
  description: string;
}

const getAdminNavItems = (prefix: string = '/beehive/admin'): AdminNavItem[] => [
  {
    name: 'Metrics',
    path: `${prefix}/metrics`,
    icon: '📊',
    description: 'System performance and analytics'
  },
  {
    name: 'Agents',
    path: `${prefix}/agents`,
    icon: '🤖',
    description: 'Agent management and monitoring'
  },
  {
    name: 'Emergency',
    path: `${prefix}/emergency`,
    icon: '🚨',
    description: 'Critical system controls'
  },
  {
    name: 'Activity',
    path: `${prefix}/activity`,
    icon: '👥',
    description: 'User sessions and auth events'
  },
  {
    name: 'Security',
    path: `${prefix}/security`,
    icon: '🛡️',
    description: 'Authentication, threats, and security metrics'
  },
  {
    name: 'Resilience',
    path: `${prefix}/resilience`,
    icon: '🦾',
    description: 'Failure detection and recovery'
  },
];

export const AdminNavigation: React.FC<{ prefix?: string }> = ({ prefix = '/beehive/admin' }) => {
  const location = useLocation();

  return (
    <div className="mb-8">
      <div className="bg-gradient-to-r from-slate-900/50 to-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2">
          <span className="text-blue-400">🛡️</span>
          Internal Dashboard Navigation
        </h2>
        
        <div data-devinid="admin-navigation-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {getAdminNavItems(prefix).map((item: AdminNavItem) => {
            const isActive = location.pathname === item.path;
            
            return (
              <Link key={item.path} to={item.path}>
                <motion.div
                  data-devinid={`admin-nav-${item.name.toLowerCase()}`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`
                    p-4 rounded-lg border transition-all duration-200 cursor-pointer
                    ${isActive 
                      ? 'bg-blue-500/20 border-blue-400/50 shadow-lg shadow-blue-500/20' 
                      : 'bg-slate-800/30 border-slate-600/30 hover:bg-slate-700/40 hover:border-slate-500/50'
                    }
                  `}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{item.icon}</span>
                    <span className={`font-medium ${isActive ? 'text-blue-300' : 'text-slate-200'}`}>
                      {item.name}
                    </span>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    {item.description}
                  </p>
                  {isActive && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-blue-400">
                      <span>●</span>
                      <span>Current Page</span>
                    </div>
                  )}
                </motion.div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};
